import type { Authenticity } from '@chicare/shared';
import { cn } from '@/lib/cn';
import { Icon } from '@/components/icons/Icon';
import { ExpiryMeter } from './ExpiryMeter';

/**
 * The Authenticity Strip — the signature element of the whole product.
 *
 * §11: "The Authenticity Strip is never collapsed behind an accordion."
 *
 * That rule is the entire component. Nigeria's problem is not that buyers
 * cannot find drug facts; it is that they cannot tell whether the pack in their
 * hand is real. Everything here is a fact the buyer can check against the
 * physical carton with their own eyes — NAFDAC number, batch code, expiry, and
 * the licensed premises that dispensed it — so it renders open, always, above
 * the fold, and there is deliberately no `collapsed` prop to add later.
 *
 * The mono typeface on the values is functional (§11): these strings are read
 * character by character against a printed panel, and a proportional font makes
 * B24F0017 and B24F0Ol7 look the same.
 */

export interface AuthenticityStripProps {
  authenticity: Authenticity;
  /** Opens the scratch-code verification sheet. Optional — §3.2 keeps it optional. */
  onVerify?: () => void;
  /** "Checked 24 Aug" — when ChiCare last confirmed the registration. */
  checkedOn?: string;
  className?: string;
}

export function AuthenticityStrip({
  authenticity,
  onVerify,
  checkedOn,
  className,
}: AuthenticityStripProps) {
  const verified = Boolean(authenticity.nafdac_reg_no) && authenticity.dispensed_by?.licence_active;

  return (
    <section
      aria-label="Authenticity"
      className={cn('overflow-hidden rounded-card border border-line bg-white', className)}
    >
      {/* Header. Green here means one thing and one thing only: verified. */}
      <header
        className={cn(
          'flex items-center gap-2 border-b px-[14px] py-[11px]',
          verified
            ? 'border-b-verified/15 bg-verified-tint'
            : 'border-b-line bg-paper',
        )}
      >
        {/* An icon ONLY when something is wrong.
            "NAFDAC verified" in green needs no tick beside it — the words and
            the colour already say it, and a mark that appears every time a
            pack is fine is a mark nobody reads. "Registration not confirmed"
            is the case worth interrupting somebody for, so that one keeps its
            warning triangle. */}
        {verified ? null : (
          <Icon name="alert" size={18} strokeWidth={1.9} className="text-muted" />
        )}
        <b
          className={cn(
            'text-[12.5px] font-bold tracking-[0.01em]',
            verified ? 'text-verified' : 'text-muted',
          )}
        >
          {verified ? 'NAFDAC verified' : 'Registration not confirmed'}
        </b>
        {checkedOn ? (
          <span className={cn('ml-auto text-[11px]', verified ? 'text-verified' : 'text-faint')}>
            Checked {checkedOn}
          </span>
        ) : null}
      </header>

      <div className="px-[14px] py-[13px]">
        <div className="mb-[11px] flex flex-wrap gap-3.5">
          <Field label="NAFDAC reg. no." value={authenticity.nafdac_reg_no} />
          <Field label="Batch no." value={authenticity.batch_no} />
        </div>

        <div className="mb-[3px] text-[9.5px] font-semibold uppercase tracking-[0.09em] text-faint">
          Expiry
        </div>
        <ExpiryMeter
          state={authenticity.expiry_state}
          monthsRemaining={authenticity.months_remaining}
          expiryLabel={authenticity.expiry_label}
        />

        {authenticity.dispensed_by ? (
          <div className="mt-[11px] flex items-center gap-2 border-t border-line-2 pt-[11px]">
            <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-blue-tint font-display text-[11px] font-bold text-blue">
              {authenticity.dispensed_by.initials}
            </span>
            <div className="min-w-0">
              <div className="text-[11px] font-bold text-ink">
                Dispensed by {authenticity.dispensed_by.name}
              </div>
              {/* The council named is the one that licenses THIS sale: PCN for
                  a human medicine, VCN for a veterinary one. The server decides
                  which, because it is the side that knows what the product is. */}
              <div className="font-mono text-[10.5px] text-faint">
                {authenticity.dispensed_by.licence_no ??
                  `${authenticity.dispensed_by.licence_label} pending`}{' '}
                · {authenticity.dispensed_by.licence_active ? 'licence active' : 'licence expired'}
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {/* §3.2: scratch-code verification stays OPTIONAL. It needs network and
          adds a minute, so it is offered, never demanded. */}
      {onVerify ? (
        <button
          type="button"
          onClick={onVerify}
          className="touch-target flex w-full items-center justify-between border-t border-line-2 px-[14px] py-[11px] text-[12.5px] font-bold text-blue hover:bg-blue-tint-2"
        >
          <span>Verify this pack</span>
          <Icon name="chevron" size={15} strokeWidth={2.4} />
        </button>
      ) : null}
    </section>
  );
}

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="min-w-0">
      <div className="mb-[3px] text-[9.5px] font-semibold uppercase tracking-[0.09em] text-faint">
        {label}
      </div>
      <div className="select-all font-mono text-[13px] font-medium tracking-[0.03em] text-ink">
        {value ?? '—'}
      </div>
    </div>
  );
}
